import type { CurrencyOptions } from '@/models/common/CurrencyOptions';
import { useSystemStore } from '@/store/systemStore';
import { useSettings } from '@/login/composables/useSettings';
import type Employment from '@sysarb/sysarb-api/models/entities/Employment';

export class CurrencyHelper {
    protected static defaultCurrency: string;

    public static currencyCount: Map<string, number> = new Map<string, number>();

    /**
    * Checks the currencies from employments and adds them to the store.
    * This function checks if no currencies have been set from employees it uses the server default.
    * @param currencyOptions a set of different currencies from employments.
    */
    public static async initGlobalCurrency(currencyTypes: Set<string>, hasCompaRatio = false): Promise<void> {
        CurrencyHelper.defaultCurrency = await CurrencyHelper.getDefaultCurrency();
        if (currencyTypes.size === 0) {
            currencyTypes.add(CurrencyHelper.defaultCurrency);
        }
        const payload: CurrencyOptions = { defaultCurrency: '', hasCompaRatio, currencyTypes: [...currencyTypes] };
        const systemStore = useSystemStore();
        systemStore.setCurrencyOptions(payload);
    }

    /**
    * initiates a global currency for the managerview.
    * The currency will either be set from what different currencies are set on the employments
    *  or use the default currency from the server if employment currencies haven't been set.
    * @param employments Employments with currencies.
    */
    public static async initGlobalCurrencyManagerView(employments: Employment[]): Promise<void> {
        const defaultCurrency = await CurrencyHelper.getDefaultCurrency();
        const currencyTypes: Set<string> = new Set();
        employments.forEach(employment => {
            const currency = (employment.currency !== '' && employment.currency != null) ? employment.currency : defaultCurrency!;
            currencyTypes.add(currency);
            if (this.currencyCount.has(currency)) {
                this.currencyCount.set(currency, this.currencyCount.get(currency)! + 1);
            } else {
                this.currencyCount.set(currency, 1);
            }
        });
        void CurrencyHelper.initGlobalCurrency(currencyTypes);
    }

    /**
    * checks if the employee has a currency set to it if not it will use the default currency.
    * @param employmentCurrency currency set on the employee.
    * @param defaultCurrency currency to use as a default.
    */
    public static getEmploymentCurrency(employmentCurrency: string): string {
        return employmentCurrency != null && employmentCurrency !== '' ? employmentCurrency : CurrencyHelper.defaultCurrency;
    }

    /**
    * Loads the default currency from the server and commits it to the store.
    */
    public static getDefaultCurrency(): string {
        const { systemSettings } = useSystemStore();
        const defaultCurrency = useSettings().instanceSettings.value?.DefaultCurrency ?? '';

        systemSettings.defaultCurrency = defaultCurrency;
        useSystemStore().setSystemSettings(systemSettings);

        return defaultCurrency;
    }

    /**
    * Returns all currency options in the system
    */
    public static getCurrencyOptions(): string[] {
        return [
            'AED',
            'AFN',
            'ALL',
            'AMD',
            'ANG',
            'AOA',
            'ARS',
            'AUD',
            'AWG',
            'AZN',
            'BAM',
            'BBD',
            'BDT',
            'BGN',
            'BHD',
            'BIF',
            'BMD',
            'BND',
            'BOB',
            'BOV',
            'BRL',
            'BSD',
            'BTN',
            'BWP',
            'BYN',
            'BZD',
            'CAD',
            'CDF',
            'CHE',
            'CHF',
            'CHW',
            'CLF',
            'CLP',
            'CNY',
            'COP',
            'COU',
            'CRC',
            'CUC',
            'CUP',
            'CVE',
            'CZK',
            'DJF',
            'DKK',
            'DOP',
            'DZD',
            'EGP',
            'ERN',
            'ETB',
            'EUR',
            'FJD',
            'FKP',
            'GBP',
            'GEL',
            'GHS',
            'GIP',
            'GMD',
            'GNF',
            'GTQ',
            'GYD',
            'HKD',
            'HNL',
            'HRK',
            'HTG',
            'HUF',
            'IDR',
            'ILS',
            'INR',
            'IQD',
            'IRR',
            'ISK',
            'JMD',
            'JOD',
            'JPY',
            'KES',
            'KGS',
            'KHR',
            'KMF',
            'KPW',
            'KRW',
            'KWD',
            'KYD',
            'KZT',
            'LAK',
            'LBP',
            'LKR',
            'LRD',
            'LSL',
            'LYD',
            'MAD',
            'MDL',
            'MGA',
            'MKD',
            'MMK',
            'MNT',
            'MOP',
            'MRU',
            'MUR',
            'MVR',
            'MWK',
            'MXN',
            'MXV',
            'MYR',
            'MZN',
            'NAD',
            'NGN',
            'NIO',
            'NOK',
            'NPR',
            'NZD',
            'OMR',
            'PAB',
            'PEN',
            'PGK',
            'PHP',
            'PKR',
            'PLN',
            'PYG',
            'QAR',
            'RON',
            'RSD',
            'RUB',
            'RWF',
            'SAR',
            'SBD',
            'SCR',
            'SDG',
            'SEK',
            'SGD',
            'SHP',
            'SLL',
            'SOS',
            'SRD',
            'SSP',
            'STN',
            'SVC',
            'SYP',
            'SZL',
            'THB',
            'TJS',
            'TMT',
            'TND',
            'TOP',
            'TRY',
            'TTD',
            'TWD',
            'TZS',
            'UAH',
            'UGX',
            'USD',
            'USN',
            'UYI',
            'UYU',
            'UZS',
            'VEF',
            'VND',
            'VUV',
            'WST',
            'XAF',
            'XCD',
            'XDR',
            'XOF',
            'XPF',
            'XSU',
            'XUA',
            'YER',
            'ZAR',
            'ZMW',
            'ZWL'
        ];
    }
}
